import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, RefreshCw, ShieldCheck, Ban, Clock,
  FilePlus, PencilLine, CheckCircle2, XCircle, UserPlus, CircleAlert, AlertCircle,
  Banknote, Undo2, PhoneCall, CalendarClock, Repeat,
  FileText, FileCheck, X, User, ExternalLink, Calendar, CreditCard,
  Building2, Shield, History
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { useToast } from '../../components/ToastProvider';
import { creditosAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import { useAuth } from '../../context/AuthContext';
import PagosTab from './PagosTab';
import CobranzaTab from './CobranzaTab';
import DocumentosTab from './DocumentosTab';
import ExpedienteTab from './ExpedienteTab';
import RenovacionModal from './RenovacionModal';
import { esHistorico, etiquetaProducto, etiquetaFrecuencia, detalleFrecuenciaObservada, rotuloFrecuencia, NO_DISPONIBLE } from '../../utils/historicos';
import '../../styles/Pages.css';
import '../../styles/Expediente.css';

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'calendario', label: 'Calendario' },
  { id: 'pagos', label: 'Pagos' },
  { id: 'cobranza', label: 'Cobranza' },
  { id: 'documentos', label: 'Documentos' },
  { id: 'expediente', label: 'Expediente' },
  { id: 'auditoria', label: 'Auditoría' }
];

const EVENTOS = {
  CREDIT_CREATED: { icono: FilePlus, texto: 'Crédito creado', tono: 'positivo' },
  CREDIT_UPDATED: { icono: PencilLine, texto: 'Crédito actualizado', tono: 'neutro' },
  CREDIT_AUTHORIZED: { icono: CheckCircle2, texto: 'Crédito autorizado', tono: 'positivo' },
  CREDIT_REJECTED: { icono: XCircle, texto: 'Crédito rechazado', tono: 'peligro' },
  CREDIT_CANCELLED: { icono: Ban, texto: 'Crédito cancelado', tono: 'peligro' },
  PARTICIPANT_ADDED: { icono: UserPlus, texto: 'Participante agregado', tono: 'neutro' },
  PARTICIPANT_UPDATED: { icono: PencilLine, texto: 'Participante actualizado', tono: 'neutro' },
  PAYMENT_REGISTERED: { icono: Banknote, texto: 'Pago registrado', tono: 'positivo' },
  PAYMENT_APPLIED: { icono: CheckCircle2, texto: 'Pago aplicado a cuotas', tono: 'positivo' },
  PAYMENT_REVERSED: { icono: Undo2, texto: 'Pago reversado', tono: 'peligro' },
  LATE_FEE_CREATED: { icono: AlertCircle, texto: 'Cargo moratorio generado', tono: 'peligro' },
  LATE_FEE_CANCELLED: { icono: CheckCircle2, texto: 'Cargo moratorio cancelado', tono: 'neutro' },
  COLLECTION_ACTION_CREATED: { icono: PhoneCall, texto: 'Gestión de cobranza registrada', tono: 'neutro' },
  PAYMENT_PROMISE_CREATED: { icono: CalendarClock, texto: 'Promesa de pago registrada', tono: 'neutro' },
  PAYMENT_PROMISE_CANCELLED: { icono: Ban, texto: 'Promesa de pago cancelada', tono: 'peligro' },
  DOCUMENT_GENERATED: { icono: FileText, texto: 'Documento contractual generado', tono: 'neutro' },
  DOCUMENT_FORMALIZED: { icono: FileCheck, texto: 'Documento contractual formalizado', tono: 'positivo' },
  DOCUMENT_DOWNLOADED: { icono: FileText, texto: 'Documento PDF descargado', tono: 'neutro' },
  DOCUMENT_PRINTED: { icono: FileText, texto: 'Documento impreso', tono: 'neutro' }
};

const moneda = formatoMoneda;

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso) return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

const fechaHora = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const iniciales = (n = '') =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';

function CreditoExpediente() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [credito, setCredito] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [cuotasCargadas, setCuotasCargadas] = useState(false);
  const [cargandoCalendario, setCargandoCalendario] = useState(false);

  const [historial, setHistorial] = useState([]);
  const [historialCargado, setHistorialCargado] = useState(false);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  
  const initialTab = searchParams.get('tab');
  const [tab, setTab] = useState(initialTab && TABS.some(t => t.id === initialTab) ? initialTab : 'resumen');
  const [autorizando, setAutorizando] = useState(false);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  const [modalRenovacion, setModalRenovacion] = useState(false);

  useEffect(() => {
    const qTab = searchParams.get('tab');
    if (qTab && TABS.some(t => t.id === qTab)) {
      setTab(qTab);
    }
  }, [searchParams]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await creditosAPI.resumen(id);
      setCredito(data);
    } catch (e) {
      setError(describirError(e));
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cargarCalendario = useCallback(async () => {
    if (cuotasCargadas || cargandoCalendario) return;
    setCargandoCalendario(true);
    try {
      const { data } = await creditosAPI.calendario(id);
      setCuotas(data?.cuotas || []);
      setCuotasCargadas(true);
    } catch (e) {
      toast.error('Error al cargar calendario de pagos');
    } finally {
      setCargandoCalendario(false);
    }
  }, [id, cuotasCargadas, cargandoCalendario, toast]);

  const cargarHistorial = useCallback(async () => {
    if (historialCargado || cargandoHistorial) return;
    setCargandoHistorial(true);
    try {
      const { data } = await creditosAPI.historial(id);
      setHistorial(data?.data || []);
      setHistorialCargado(true);
    } catch (e) {
      toast.error('Error al cargar bitácora de auditoría');
    } finally {
      setCargandoHistorial(false);
    }
  }, [id, historialCargado, cargandoHistorial, toast]);

  const handleTabChange = (nuevoTab) => {
    setTab(nuevoTab);
    if (nuevoTab === 'calendario' && !cuotasCargadas) {
      cargarCalendario();
    }
    if (nuevoTab === 'auditoria' && !historialCargado) {
      cargarHistorial();
    }
  };

  const autorizar = async () => {
    setAutorizando(true);
    try {
      const { data } = await creditosAPI.autorizar(id);
      setCredito(data);
      if (cuotasCargadas) {
        const cal = await creditosAPI.calendario(id);
        setCuotas(cal.data?.cuotas || []);
      }
      if (historialCargado) {
        const h = await creditosAPI.historial(id);
        setHistorial(h.data?.data || []);
      }
      toast.exito('Crédito autorizado correctamente.');
    } catch (e) {
      toast.error(describirError(e).mensaje);
    } finally {
      setAutorizando(false);
    }
  };

  const cambiarEstado = async (estado) => {
    const motivo = window.prompt(estado === 'RECHAZADO' ? 'Motivo del rechazo (opcional):' : 'Motivo de la cancelación (opcional):') || undefined;
    try {
      const { data } = await creditosAPI.actualizar(id, { estado, motivo });
      setCredito(data);
      if (historialCargado) {
        const h = await creditosAPI.historial(id);
        setHistorial(h.data?.data || []);
      }
      toast.exito(estado === 'RECHAZADO' ? 'Crédito rechazado.' : 'Crédito cancelado.');
    } catch (e) {
      toast.error(describirError(e).mensaje);
    }
  };

  if (cargando) {
    return (
      <div className="page-container credit-expediente-page">
        <div style={{ height: '32px', width: '200px', background: 'var(--border, #e2e8f0)', borderRadius: '6px', marginBottom: '1rem', opacity: 0.6 }} />
        <div className="exp-header-compact" style={{ minHeight: '100px', opacity: 0.7 }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--border, #cbd5e1)' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ height: '20px', width: '260px', background: 'var(--border, #cbd5e1)', borderRadius: '4px' }} />
            <div style={{ height: '14px', width: '380px', background: 'var(--border, #e2e8f0)', borderRadius: '4px' }} />
          </div>
        </div>
        <div className="exp-kpi-bar" style={{ marginTop: '1rem', opacity: 0.7 }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className="exp-kpi-item" style={{ minHeight: '80px' }}>
              <div style={{ height: '12px', width: '60%', background: 'var(--border, #cbd5e1)', borderRadius: '3px', marginBottom: '8px' }} />
              <div style={{ height: '18px', width: '80%', background: 'var(--border, #e2e8f0)', borderRadius: '4px' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !credito) {
    return (
      <div className="page-container">
        <div className="estado-vacio">
          <span className="estado-vacio-icono is-error">
            <AlertTriangle size={20} strokeWidth={1.75} />
          </span>
          <p className="estado-vacio-titulo">No fue posible cargar el expediente del crédito.</p>
          <p className="estado-vacio-texto">{error?.mensaje || 'Error al obtener datos.'}</p>
          <button className="btn btn-secondary" onClick={cargar}>
            <RefreshCw size={15} strokeWidth={1.75} />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const c = credito;
  const esBorrador = c.estado === 'BORRADOR';
  const puedeAutorizar = esBorrador && c.producto?.calculoValidado && hasPermission('CREDITOS_AUTORIZAR');
  const tipoReal = c.tipoCreditoCanonico || (c.tipoCredito === 'PERSONAL' ? 'INDIVIDUAL' : c.tipoCredito);
  const grupoColocadora = c.grupoNombre || c.colocadoraNombre || (c.agrupacion ? c.agrupacion.nombre : null);
  const esHist = esHistorico(c);
  const pagadoHist = esHist ? c.pagadoALaFecha : null;
  const saldoActual = Number(c.saldoActual ?? 0);
  const saldoVencidoReal = Number(c.saldoVencidoReal ?? 0);
  const saldoPorVencer = Number(c.saldoPorVencer ?? 0);
  const diasAtraso = Number(c.diasAtraso ?? 0);
  const titularNombre = c.titular?.nombreCompleto || c.nombreGrupo || 'Sin titular';
  const titularId = c.clienteTitularId || c.titular?.id;

  return (
    <div className="page-container credit-expediente-page">
      {/* Botón Volver */}
      <button className="btn btn-ghost volver" onClick={() => navigate('/creditos')} style={{ marginBottom: '0.75rem' }}>
        <ArrowLeft size={16} strokeWidth={1.75} />
        Volver a cartera de créditos
      </button>

      {/* ── 1. CABECERA COMPACTA DEL EXPEDIENTE DE CRÉDITO ── */}
      <header className="exp-header-compact">
        <div className="exp-avatar-wrap">
          <span className="exp-avatar-badge">{iniciales(titularNombre)}</span>
        </div>

        <div className="exp-main-info">
          <div className="exp-title-row">
            <h2 className="exp-client-name">{titularNombre}</h2>
            <StatusBadge value={c.estado} />
            <span className={`entity-badge entity-badge--${tipoReal.toLowerCase()}`}>
              {tipoReal}
            </span>
          </div>

          <div className="exp-badges-meta">
            <span className="exp-meta-item">
              <strong className="exp-meta-label">FOLIO:</strong>
              <span className="font-mono font-semibold" style={{ color: 'var(--midas-teal, #0f766e)' }}>{c.folio}</span>
            </span>

            {c.producto && (
              <span className="exp-meta-item">
                <strong className="exp-meta-label">PRODUCTO:</strong> <span data-testid="exp-producto">{etiquetaProducto(c)}</span>
              </span>
            )}

            <span className="exp-meta-item">
              <strong className="exp-meta-label">{rotuloFrecuencia(c, 'FRECUENCIA')}:</strong> <span data-testid="exp-frecuencia">{etiquetaFrecuencia(c, 'Semanal')}{detalleFrecuenciaObservada(c)}</span>
            </span>

            {/* Agrupación / Colocadora */}
            {c.agrupacion ? (
              <button
                type="button"
                className="exp-meta-item exp-agrupacion-pill"
                onClick={() => navigate(`/agrupaciones/${c.agrupacion.id}`)}
                title={`Ver agrupación ${c.agrupacion.folio}`}
              >
                <Building2 size={13} strokeWidth={2} className="exp-inline-icon" />
                {c.agrupacion.tipoAgrupacion || 'GRUPO'}: {c.agrupacion.nombre} ({c.agrupacion.folio})
              </button>
            ) : grupoColocadora ? (
              <span className="exp-meta-item exp-agrupacion-pill">
                <Building2 size={13} strokeWidth={2} className="exp-inline-icon" />
                {grupoColocadora}
              </span>
            ) : null}

            {/* Renovación anterior badge */}
            {c.creditoAnterior && (
              <button
                type="button"
                className="exp-renewal-badge"
                onClick={() => c.creditoAnterior.id ? navigate(`/creditos/${c.creditoAnterior.id}`) : null}
                title="Crédito anterior del cual este es renovación"
              >
                <Repeat size={12} strokeWidth={2} />
                <span>Renovación de: {c.creditoAnterior.folio}</span>
              </button>
            )}

            {/* Renovado como nuevo crédito badge */}
            {c.creditoRenovado && (
              <button
                type="button"
                className="exp-renewal-badge"
                style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb', borderColor: 'rgba(59, 130, 246, 0.25)' }}
                onClick={() => navigate(`/creditos/${c.creditoRenovado.id}`)}
                title="Crédito nuevo generado por renovación"
              >
                <Repeat size={12} strokeWidth={2} />
                <span>Renovado como: {c.creditoRenovado.folio}</span>
              </button>
            )}
          </div>
        </div>

        {/* Acciones de Cabecera según RBAC */}
        <div className="exp-header-actions">
          {esBorrador && hasPermission('CREDITOS_EDITAR') && (
            <button className="btn btn-danger btn-sm" onClick={() => cambiarEstado('RECHAZADO')}>
              <XCircle size={15} strokeWidth={1.75} />
              Rechazar
            </button>
          )}

          {esBorrador && (
            <button
              className="btn btn-primary btn-sm"
              onClick={autorizar}
              disabled={!puedeAutorizar || autorizando}
              title={!c.producto?.calculoValidado ? 'El producto no tiene cálculo validado' : undefined}
            >
              <ShieldCheck size={15} strokeWidth={1.75} />
              {autorizando ? 'Autorizando…' : 'Autorizar crédito'}
            </button>
          )}

          {/* Workflow de Renovación */}
          {c.estado !== 'CANCELADO' && c.estado !== 'RECHAZADO' && !c.creditoRenovado && hasPermission('CREDITOS_CREAR') && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setModalRenovacion(true)}
              title="Iniciar workflow de renovación de crédito"
            >
              <Repeat size={15} strokeWidth={1.75} />
              Renovar Crédito
            </button>
          )}

          {c.estado === 'AUTORIZADO' && hasPermission('CREDITOS_EDITAR') && (
            <button className="btn btn-danger btn-sm" onClick={() => cambiarEstado('CANCELADO')}>
              <Ban size={15} strokeWidth={1.75} />
              Cancelar
            </button>
          )}
        </div>
      </header>

      {esBorrador && !c.producto?.calculoValidado && (
        <div className="data-notice" style={{ marginBottom: '1rem' }}>
          <CircleAlert size={16} strokeWidth={1.75} />
          <span>
            El producto <strong>{c.producto?.legacyName || c.producto?.nombre}</strong> todavía no tiene un
            cálculo validado. Este crédito puede existir como BORRADOR, pero no puede autorizarse ni generar
            calendario definitivo hasta contar con parametrización confirmada.
          </span>
        </div>
      )}

      {/* ── 2. KPI STRIP SUPERIOR (8 TARJETAS HOMOLOGADAS) ── */}
      <section className="exp-kpi-bar" aria-label="Resumen Financiero del Crédito">
        {/* 1. Saldo Actual */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO ACTUAL</span>
          <div className="exp-kpi-mainval text-teal">
            {moneda(saldoActual)}
          </div>
          <span className="exp-kpi-footer">Cartera viva</span>
        </div>

        {/* 2. Saldo Vencido Real */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO VENCIDO REAL</span>
          <div className={`exp-kpi-mainval ${saldoVencidoReal > 0 ? 'text-danger' : 'text-success'}`}>
            {moneda(saldoVencidoReal)}
          </div>
          <span className="exp-kpi-footer">
            {saldoVencidoReal > 0 ? `${c.cuotasVencidas || 0} cuotas vencidas` : 'Al corriente'}
          </span>
        </div>

        {/* 3. Saldo por Vencer */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO POR VENCER</span>
          <div className="exp-kpi-mainval">
            {moneda(saldoPorVencer)}
          </div>
          <span className="exp-kpi-footer">Vigente a futuro</span>
        </div>

        {/* 4. Próximo Pago */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">PRÓXIMO PAGO</span>
          <div className="exp-kpi-mainval">
            {c.importeProximoPago != null ? moneda(c.importeProximoPago) : '—'}
          </div>
          <span className="exp-kpi-footer">
            {c.proximoVencimiento ? `Vence: ${fechaCorta(c.proximoVencimiento)}` : 'Sin pagos pendientes'}
          </span>
        </div>

        {/* 5. Próximo Vencimiento */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">PRÓXIMO VENCIMIENTO</span>
          <div className="exp-kpi-mainval" style={{ fontSize: '1rem', fontWeight: 600 }}>
            {c.proximoVencimiento ? fechaCorta(c.proximoVencimiento) : '—'}
          </div>
          <span className="exp-kpi-footer">Fecha exigible</span>
        </div>

        {/* 6. Documentos Vencidos */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">DOCUMENTOS VENCIDOS</span>
          <div className={`exp-kpi-mainval ${c.cuotasVencidas > 0 ? 'text-danger' : 'text-success'}`}>
            {c.cuotasVencidas || 0} <span className="exp-kpi-denom">cuotas</span>
          </div>
          <span className="exp-kpi-footer">Cuotas en mora</span>
        </div>

        {/* 7. Días de Atraso */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">DÍAS DE ATRASO</span>
          <div className={`exp-kpi-mainval ${diasAtraso > 0 ? 'text-danger' : 'text-success'}`}>
            {diasAtraso} <span className="exp-kpi-denom">días</span>
          </div>
          <span className="exp-kpi-footer">Desde el vencimiento pendiente más antiguo</span>
        </div>

        {/* 8. Pagado a la Fecha */}
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">{pagadoHist ? 'COBROS REGISTRADOS' : 'PAGADO A LA FECHA'}</span>
          {pagadoHist && !pagadoHist.disponible ? (
            <>
              <div className="exp-kpi-mainval" data-testid="kpi-pagado" title={pagadoHist.motivo}>{NO_DISPONIBLE}</div>
              <span className="exp-kpi-footer">Sin pagos migrados registrados (sin total de control)</span>
            </>
          ) : pagadoHist ? (
            <>
              <div className="exp-kpi-mainval" data-testid="kpi-pagado" title={pagadoHist.nota}>
                {moneda(pagadoHist.importe)}
              </div>
              <span className="exp-kpi-footer">
                {pagadoHist.cantidadMigrados} cobros migrados{Number(pagadoHist.importeNativo) > 0 ? ` + ${moneda(pagadoHist.importeNativo)} nativos` : ''} · sin total de control
              </span>
            </>
          ) : (
            <>
              <div className="exp-kpi-mainval" data-testid="kpi-pagado">
                {moneda(c.totalAplicado || 0)}
              </div>
              <span className="exp-kpi-footer">
                De {moneda(c.totalProgramado || c.totalPagarSnapshot || c.montoSolicitado)} total
              </span>
            </>
          )}
        </div>
      </section>

      {/* ── 3. NAVEGACIÓN DE PESTAÑAS ── */}
      <nav className="exp-tabs-nav" role="tablist">
        {TABS.filter((t) => !['documentos', 'expediente'].includes(t.id) || hasPermission('EXPEDIENTES_VER')).map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`exp-tab-btn ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => handleTabChange(t.id)}
          >
            {t.label}
            {t.id === 'calendario' && (cuotas.length > 0 || c.numeroPagosSnapshot) && (
              <span className="exp-tab-counter">{cuotas.length || c.numeroPagosSnapshot}</span>
            )}
            {t.id === 'auditoria' && historial.length > 0 && (
              <span className="exp-tab-counter">{historial.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ── 4. CONTENIDO DE PESTAÑAS ── */}

      {/* ── TAB 1: RESUMEN ── */}
      {tab === 'resumen' && (
        <div className="exp-sections-grid">
          {/* Tarjeta A: Datos del Crédito */}
          <div className="exp-card">
            <div className="exp-card-header">
              <CreditCard size={16} className="exp-card-icon" />
              <h3>Datos del Crédito</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Folio</dt>
                  <dd className="exp-dd-highlight font-mono">{c.folio}</dd>
                </div>
                <div>
                  <dt>Estado</dt>
                  <dd><StatusBadge value={c.estado} /></dd>
                </div>
                <div>
                  <dt>Modalidad</dt>
                  <dd>
                    <span className={`entity-badge entity-badge--${tipoReal.toLowerCase()}`}>{tipoReal}</span>
                  </dd>
                </div>
                <div>
                  <dt>Producto</dt>
                  <dd className="font-semibold">{etiquetaProducto(c)}</dd>
                </div>
                <div>
                  <dt>{esHist ? 'Frecuencia observada (calendario)' : 'Frecuencia de Pago'}</dt>
                  <dd>{etiquetaFrecuencia(c, 'Semanal')}{detalleFrecuenciaObservada(c)}</dd>
                </div>
                <div>
                  <dt>Asesor / Agente</dt>
                  <dd>{c.agente || 'No asignado'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Tarjeta B: Cliente Titular */}
          <div className="exp-card">
            <div className="exp-card-header">
              <User size={16} className="exp-card-icon" />
              <h3>Cliente Titular</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Nombre Completo</dt>
                  <dd className="font-semibold">{titularNombre}</dd>
                </div>
                <div>
                  <dt>Folio Cliente</dt>
                  <dd className="font-mono">{c.titular?.folio || '—'}</dd>
                </div>
                {c.titular?.telefonoPrincipal && (
                  <div>
                    <dt>Teléfono</dt>
                    <dd>{c.titular.telefonoPrincipal}</dd>
                  </div>
                )}
                {c.titular?.estado && (
                  <div>
                    <dt>Estado del Cliente</dt>
                    <dd><StatusBadge value={c.titular.estado} /></dd>
                  </div>
                )}
              </dl>
              {titularId && (
                <div style={{ marginTop: '0.875rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(`/clientes/${titularId}`)}
                  >
                    <ExternalLink size={14} />
                    Ver Expediente del Cliente
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tarjeta C: Agrupación / Colocadora (si aplica) */}
          {(c.agrupacion || grupoColocadora) && (
            <div className="exp-card">
              <div className="exp-card-header">
                <Building2 size={16} className="exp-card-icon" />
                <h3>Agrupación / Colocadora</h3>
              </div>
              <div className="exp-card-body">
                <dl className="exp-dl-grid">
                  <div>
                    <dt>Nombre</dt>
                    <dd className="font-semibold">{c.agrupacion?.nombre || grupoColocadora}</dd>
                  </div>
                  <div>
                    <dt>Tipo</dt>
                    <dd>{c.agrupacion?.tipoAgrupacion || tipoReal}</dd>
                  </div>
                  {c.agrupacion?.folio && (
                    <div>
                      <dt>Folio Agrupación</dt>
                      <dd className="font-mono">{c.agrupacion.folio}</dd>
                    </div>
                  )}
                </dl>
                {c.agrupacion?.id && (
                  <div style={{ marginTop: '0.875rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/agrupaciones/${c.agrupacion.id}`)}
                    >
                      <ExternalLink size={14} />
                      Ver Ficha de Agrupación
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tarjeta D: Condiciones Contractuales */}
          <div className="exp-card">
            <div className="exp-card-header">
              <Calendar size={16} className="exp-card-icon" />
              <h3>Información Contractual</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Monto Original Solicitado</dt>
                  <dd className="exp-dd-highlight">{moneda(c.montoSolicitado)}</dd>
                </div>
                <div>
                  <dt>Total Contractual Programado</dt>
                  <dd className="font-semibold" title={esHist ? 'Crédito histórico: el total contractual original no se migró (el calendario migrado solo contiene cuotas pendientes).' : undefined}>
                    {esHist ? NO_DISPONIBLE : moneda(c.totalPagarSnapshot != null ? c.totalPagarSnapshot : c.totalProgramado)}
                  </dd>
                </div>
                <div>
                  <dt>{esHist ? 'Cuotas en el calendario migrado' : 'Número de Pagos'}</dt>
                  <dd>{esHist ? (c.cuotas?.length ?? '—') : (c.numeroPagosSnapshot || c.producto?.numeroPagos || c.cuotas?.length || '—')}</dd>
                </div>
                <div>
                  <dt>Pago Regular</dt>
                  <dd>{c.pagoRegularSnapshot != null ? moneda(c.pagoRegularSnapshot) : '—'}</dd>
                </div>
                {c.ultimaCuotaSnapshot != null && (
                  <div>
                    <dt>Última Cuota</dt>
                    <dd>{moneda(c.ultimaCuotaSnapshot)}</dd>
                  </div>
                )}
                {c.interesPorcentajeSnapshot != null && (
                  <div>
                    <dt>Tasa de Interés</dt>
                    <dd>{c.interesPorcentajeSnapshot}%</dd>
                  </div>
                )}
                <div>
                  <dt>Fecha Desembolso</dt>
                  <dd>{fechaCorta(c.fechaDesembolso)}</dd>
                </div>
                <div>
                  <dt>Primer Vencimiento</dt>
                  <dd>{fechaCorta(c.primerVencimientoSnapshot || c.cuotas?.[0]?.fechaVencimiento)}</dd>
                </div>
                <div>
                  <dt>Fin Contractual</dt>
                  <dd>{fechaCorta(c.fechaFinContractual || c.cuotas?.[c.cuotas.length - 1]?.fechaVencimiento)}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Tarjeta E: Situación Actual y Cartera */}
          <div className="exp-card">
            <div className="exp-card-header">
              <Shield size={16} className="exp-card-icon" />
              <h3>Situación Actual de Cartera</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Saldo Actual Exigible</dt>
                  <dd className="exp-dd-highlight text-teal">{moneda(saldoActual)}</dd>
                </div>
                <div>
                  <dt>Saldo Vencido Real</dt>
                  <dd className={saldoVencidoReal > 0 ? 'text-danger font-semibold' : 'text-success font-semibold'}>
                    {moneda(saldoVencidoReal)}
                  </dd>
                </div>
                <div>
                  <dt>Saldo por Vencer</dt>
                  <dd>{moneda(saldoPorVencer)}</dd>
                </div>
                <div>
                  <dt>Días de Atraso</dt>
                  <dd className={diasAtraso > 0 ? 'text-danger font-semibold' : 'text-success'}>
                    {diasAtraso} días
                  </dd>
                </div>
                <div>
                  <dt>Cuotas Vencidas</dt>
                  <dd className={c.cuotasVencidas > 0 ? 'text-danger font-semibold' : 'text-success'}>
                    {c.cuotasVencidas || 0} cuotas
                  </dd>
                </div>
                <div>
                  <dt>Mora Devengada</dt>
                  <dd className={c.conMora ? 'text-danger font-semibold' : 'text-muted'}>
                    {c.conMora ? moneda(c.saldoMoratorios || c.mora) : '$0.00'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Tarjeta F: Garantía Registrada */}
          {(c.tipoGarantia || c.garantiaDescripcion) && (
            <div className="exp-card">
              <div className="exp-card-header">
                <ShieldCheck size={16} className="exp-card-icon" />
                <h3>Garantía Registrada</h3>
              </div>
              <div className="exp-card-body">
                <dl className="exp-dl-grid">
                  <div>
                    <dt>Tipo de Garantía</dt>
                    <dd>{c.tipoGarantia || 'No especificado'}</dd>
                  </div>
                  <div>
                    <dt>Descripción</dt>
                    <dd>{c.garantiaDescripcion || '—'}</dd>
                  </div>
                  {c.valorGarantia != null && (
                    <div>
                      <dt>Valor Estimado</dt>
                      <dd>{moneda(c.valorGarantia)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: CALENDARIO ── */}
      {tab === 'calendario' && (
        <div className="exp-panel-table">
          <div className="exp-table-header">
            <h3>Calendario de Pagos ({cuotas.length || c.numeroPagosSnapshot || 0} cuotas)</h3>
            <p>{esBorrador ? 'El calendario final se genera al autorizar el crédito.' : 'Desglose cronológico de cuotas programadas, importes pagados y saldos pendientes.'}</p>
          </div>

          {cargandoCalendario ? (
            <div className="loading" style={{ padding: '2.5rem 0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
              <RefreshCw className="spin" size={20} />
              <span>Cargando calendario de cuotas…</span>
            </div>
          ) : (!cuotas || cuotas.length === 0) ? (
            <div className="estado-vacio">
              <span className="estado-vacio-icono"><Clock size={20} strokeWidth={1.75} /></span>
              <p className="estado-vacio-texto">Sin calendario generado todavía.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Vencimiento</th>
                    <th style={{ textAlign: 'right' }}>Importe Programado</th>
                    <th style={{ textAlign: 'right' }}>Importe Pagado</th>
                    <th style={{ textAlign: 'right' }}>Saldo Cuota</th>
                    <th style={{ textAlign: 'right' }}>Mora</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {cuotas.map((cu) => {
                    const pagada = cu.estadoCuota === 'PAGADA';
                    const parcial = cu.estadoCuota === 'PARCIAL';
                    const vencida = cu.estadoCuota === 'VENCIDA';

                    return (
                      <tr
                        key={cu.id}
                        className="fila-clicable"
                        onClick={() => setCuotaSeleccionada(cu)}
                        title="Clic para ver aplicaciones de pago de esta cuota"
                      >
                        <td className="font-mono font-bold">#{cu.numeroPago}</td>
                        <td>{fechaCorta(cu.fechaVencimiento)}</td>
                        <td style={{ textAlign: 'right' }}>{moneda(cu.importeProgramado)}</td>
                        <td style={{ textAlign: 'right', color: Number(cu.importePagado || 0) > 0 ? 'var(--color-success, #16a34a)' : 'inherit' }}>
                          {moneda(cu.importePagado || 0)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600' }}>
                          {moneda(cu.saldoCuota != null ? cu.saldoCuota : cu.saldoProgramadoCredito)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {cu.mora && Number(cu.mora) > 0 ? (
                            <span className="text-danger font-mono font-semibold">{moneda(cu.mora)}</span>
                          ) : (
                            <span className="cell-muted font-mono">$0.00</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${pagada ? 'is-activo' : vencida ? 'is-cancelado' : parcial ? 'is-autorizado' : 'is-borrador'}`}>
                            {cu.estadoCuota || cu.estado}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {cu.opening && (
                              <span className="exp-seed-pill" title={`Saldo al corte: ${moneda(cu.opening.outstandingAtCutoff)}`}>
                                Migrado
                              </span>
                            )}
                            {cu.trasladadoARemanente && (
                              <span className="exp-seed-pill" title={`El saldo no cobrado (${moneda(cu.trasladadoARemanente)}) pasa a la cuota remanente (IDPg ${cu.remanenteIdPg}); esta cuota NO está pagada por completo`}>
                                Parcial: resto en remanente
                              </span>
                            )}
                            {cu.esRemanente && (
                              <span className="exp-seed-pill" title="Cuota nueva por el saldo no cobrado de un pago parcial del sistema anterior">
                                Remanente
                              </span>
                            )}
                            <span className="cell-secondary" style={{ fontSize: '0.75rem' }}>
                              {(cu.aplicaciones?.length || 0)} pago(s)
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: PAGOS ── */}
      {tab === 'pagos' && (
        <PagosTab credito={c} onCreditoActualizado={setCredito} />
      )}

      {/* ── TAB 4: COBRANZA ── */}
      {tab === 'cobranza' && (
        <CobranzaTab credito={c} />
      )}

      {/* ── TAB 5: DOCUMENTOS ── */}
      {tab === 'documentos' && hasPermission('EXPEDIENTES_VER') && (
        <DocumentosTab creditoId={id} credito={c} />
      )}

      {/* ── TAB 6: EXPEDIENTE DIGITAL & KYC ── */}
      {tab === 'expediente' && hasPermission('EXPEDIENTES_VER') && (
        <ExpedienteTab creditoId={id} />
      )}

      {/* ── TAB 7: AUDITORÍA (SOLO ADMIN / TRAZABILIDAD TÉCNICA) ── */}
      {tab === 'auditoria' && (
        <div className="exp-sections-grid">
          {/* Procedencia Legacy */}
          <div className="exp-card">
            <div className="exp-card-header">
              <History size={16} className="exp-card-icon" />
              <h3>Procedencia de Migración Legacy</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Origen del Registro</dt>
                  <dd className="font-semibold text-primary">{c.origenRegistro || 'ORIGINADO_MIDAS'}</dd>
                </div>
                <div>
                  <dt>Legacy Crédito ID</dt>
                  <dd className="font-mono">{c.legacyCreditoId || '—'}</dd>
                </div>
                <div>
                  <dt>Legacy Crédito Anterior</dt>
                  <dd className="font-mono">{c.legacyCreditoAnteriorId || '—'}</dd>
                </div>
                <div>
                  <dt>Lote de Migración</dt>
                  <dd className="text-muted" style={{ fontSize: '0.75rem' }}>{c.migracionLoteId || '—'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Parámetros Técnicos */}
          <div className="exp-card">
            <div className="exp-card-header">
              <Shield size={16} className="exp-card-icon" />
              <h3>Metadatos y Parámetros Técnicos</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Fecha de Alta Técnica</dt>
                  <dd>{fechaHora(c.createdAt)}</dd>
                </div>
                <div>
                  <dt>Última Actualización</dt>
                  <dd>{fechaHora(c.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Creado Por</dt>
                  <dd className="text-muted" style={{ fontSize: '0.8125rem' }}>{c.createdBy || '—'}</dd>
                </div>
                <div>
                  <dt>Actualizado Por</dt>
                  <dd className="text-muted" style={{ fontSize: '0.8125rem' }}>{c.updatedBy || '—'}</dd>
                </div>
                <div>
                  <dt>Observaciones</dt>
                  <dd>{c.observaciones || 'Sin observaciones'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Bitácora de Eventos de Auditoría */}
          <div className="exp-card" style={{ gridColumn: '1 / -1' }}>
            <div className="exp-card-header">
              <Clock size={16} className="exp-card-icon" />
              <h3>Bitácora Inmutable de Auditoría ({historial.length})</h3>
            </div>
            <div className="exp-card-body">
              {cargandoHistorial ? (
                <div className="loading" style={{ padding: '2rem 0', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
                  <RefreshCw className="spin" size={20} />
                  <span>Cargando bitácora de auditoría…</span>
                </div>
              ) : historial.length === 0 ? (
                <p className="exp-empty-text">Sin eventos de auditoría registrados.</p>
              ) : (
                <ol className="timeline">
                  {historial.map((e) => {
                    const cfg = EVENTOS[e.action] || { icono: Clock, texto: e.action, tono: 'neutro' };
                    const Icono = cfg.icono;
                    return (
                      <li key={e.id} className="timeline-item">
                        <span className={`timeline-punto is-${cfg.tono}`}>
                          <Icono size={14} strokeWidth={1.75} />
                        </span>
                        <div className="timeline-cuerpo">
                          <p className="timeline-fecha">{fechaCorta(e.createdAt)}</p>
                          <p className="timeline-texto">{cfg.texto}</p>
                          {e.newData?.motivo && (
                            <p className="timeline-detalle">Motivo: {e.newData.motivo}</p>
                          )}
                          {e.newData?.totalPagar && (
                            <p className="timeline-detalle">Total contractual: {moneda(e.newData.totalPagar)}</p>
                          )}
                          <p className="timeline-actor">{e.performedBy} · {fechaHora(e.createdAt)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal / Drawer de Detalle de Cuota y Aplicaciones de Pago ── */}
      {cuotaSeleccionada && (
        <div className="drawer-backdrop" onClick={() => setCuotaSeleccionada(null)}>
          <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Detalle de Cuota #{cuotaSeleccionada.numeroPago}</h3>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={() => setCuotaSeleccionada(null)}
                aria-label="Cerrar detalle"
              >
                <X size={18} />
              </button>
            </div>

            <div className="drawer-body">
              <div className="exp-card" style={{ marginBottom: '1rem' }}>
                <div className="exp-card-header">
                  <h4>Información de la Cuota</h4>
                </div>
                <div className="exp-card-body">
                  <dl className="exp-dl-grid">
                    <div><dt>Número de Cuota</dt><dd className="font-mono font-bold">#{cuotaSeleccionada.numeroPago}</dd></div>
                    <div><dt>Fecha de Vencimiento</dt><dd>{fechaCorta(cuotaSeleccionada.fechaVencimiento)}</dd></div>
                    <div><dt>Importe Programado</dt><dd className="font-bold">{moneda(cuotaSeleccionada.importeProgramado)}</dd></div>
                    <div><dt>Total Pagado / Aplicado</dt><dd className="text-success font-bold">{moneda(cuotaSeleccionada.importePagado || 0)}</dd></div>
                    <div><dt>Saldo Pendiente</dt><dd className="font-bold">{moneda(cuotaSeleccionada.saldoCuota)}</dd></div>
                    <div><dt>Mora Devengada</dt><dd>{cuotaSeleccionada.mora ? <span className="text-danger font-bold">{moneda(cuotaSeleccionada.mora)}</span> : '$0.00'}</dd></div>
                    <div><dt>Estado de la Cuota</dt><dd><StatusBadge value={cuotaSeleccionada.estadoCuota || cuotaSeleccionada.estado} /></dd></div>
                  </dl>
                </div>
              </div>

              {cuotaSeleccionada.opening && (
                <div className="exp-card" style={{ marginBottom: '1rem', background: 'rgba(176, 125, 40, 0.05)', borderColor: 'rgba(176, 125, 40, 0.2)' }}>
                  <div className="exp-card-header">
                    <h4>Estado de Apertura (Migración)</h4>
                  </div>
                  <div className="exp-card-body">
                    <dl className="exp-dl-grid">
                      <div><dt>Saldo al Corte</dt><dd className="font-bold">{moneda(cuotaSeleccionada.opening.outstandingAtCutoff)}</dd></div>
                      <div><dt>Pagado al Corte</dt><dd>{moneda(cuotaSeleccionada.opening.paidAtCutoff)}</dd></div>
                      <div><dt>Estatus al Corte</dt><dd>{cuotaSeleccionada.opening.statusAtCutoff}</dd></div>
                      <div><dt>Fecha de Corte</dt><dd>{fechaCorta(cuotaSeleccionada.opening.cutoffDate)}</dd></div>
                    </dl>
                  </div>
                </div>
              )}

              <div className="exp-card">
                <div className="exp-card-header">
                  <h4>Aplicaciones de Pago Registradas ({cuotaSeleccionada.aplicaciones?.length || 0})</h4>
                </div>
                <div className="exp-card-body">
                  {(!cuotaSeleccionada.aplicaciones || cuotaSeleccionada.aplicaciones.length === 0) ? (
                    <p className="exp-empty-text">Sin pagos aplicados a esta cuota.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="data-table" style={{ fontSize: '0.8125rem' }}>
                        <thead>
                          <tr>
                            <th>Folio Pago</th>
                            <th>Fecha</th>
                            <th style={{ textAlign: 'right' }}>Importe Aplicado</th>
                            <th>Usuario</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cuotaSeleccionada.aplicaciones.map((ap) => (
                            <tr key={ap.id}>
                              <td className="font-mono font-bold text-teal">{ap.pago?.folioPago || '—'}</td>
                              <td>{fechaCorta(ap.pago?.fechaPago)}</td>
                              <td style={{ textAlign: 'right' }} className="font-bold text-success">{moneda(ap.importeAplicado)}</td>
                              <td>{ap.pago?.registradoPor || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setCuotaSeleccionada(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Workflow de Renovación ── */}
      {modalRenovacion && (
        <RenovacionModal
          credito={c}
          onClose={() => setModalRenovacion(false)}
          onRenovacionExitosa={(nuevo) => {
            cargar();
            toast.exito(`Renovación creada con éxito: ${nuevo.folio}`);
          }}
        />
      )}
    </div>
  );
}

export default CreditoExpediente;
